import { Injectable, inject, InjectionToken } from '@angular/core';
import { IrisQueryParams, IrisQueryParamsBuilder } from '@iris/api-query';
import { TranslateService } from '@ngx-translate/core';
import { IrisProjectI } from '@iris/common/models';
import { PhaseNames } from '@iris/common/models/IrisProjectPhase';
import { emptyIrisPage, IrisPage } from '@iris/common/models/page';
import { IrisSkSelectFieldSearchEngine } from '@iris/common/modules/fields/sk-select-field/search-engine.sk';
import { IrisProjectGroup } from './models/project-group';
import { IrisProjectsService } from '@iris/common/services/projects.service';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, finalize, map, startWith } from 'rxjs/operators';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { BindNgSelectEvent } from '@iris/common/modules/fields/sk-select-field/decorators.sk';
import { handleInfiniteScroll$, handleSearch$, handleTypeahead$, SearchContext, searchProcessor } from '@iris/common/modules/fields/sk-select-field/search-processing.sk';
import { objectEntries } from '@iris/common/utils/object.utils';

export interface FieldOptions {
  showCostCenter: boolean;
  hideRecentProjects: boolean;
}
export interface FilterProjectsBy {
  companyId: string[] | null;
}

interface SearchOptions {
  includeCompletedProjectsInSearch: boolean;
  searchOnlyInProjects: number[] | null;
  excludeProjects: number[] | null;
  filterBy: FilterProjectsBy | null;
  includeInactiveProjects: boolean;
}

type ProjectSearchContext<TItem = IrisProjectGroup> = SearchContext<TItem> & { searchOptions?: SearchOptions };

const PAGE_SIZE = 10;
const ONLY_FIELDS: (keyof IrisProjectI | keyof IrisPage<unknown>)[] = ['id', 'name', 'costCenter', 'strathekNumber', 'count', 'elements', 'companyId'];

export type ProjectSelectEngineApi = Pick<IrisProjectsService, 'getProjectsPage' | 'getById'>;
export const PROJECT_SELECT_ENGINE_API = new InjectionToken<ProjectSelectEngineApi>('PROJECT_SELECT_ENGINE_API');

@Injectable()
export class IrisProjectSelectEngine implements IrisSkSelectFieldSearchEngine<IrisProjectGroup, number> {
  private readonly translate = inject(TranslateService);
  private readonly projectsApiService: ProjectSelectEngineApi = inject(PROJECT_SELECT_ENGINE_API, { optional: true }) ?? inject(IrisProjectsService);
  private readonly globalSandbox = inject(IrisGlobalSandbox);

  readonly typeahead = new BehaviorSubject('');
  readonly searchString$ = this.typeahead.pipe(
    map(value => value?.trim() ?? ''),
  );

  private readonly loadingSubject = new Subject<boolean>();
  readonly loading$ = this.loadingSubject.asObservable();

  @BindNgSelectEvent('scrollToEnd')
  private readonly scrollToEndSubject = new Subject<void>();

  @BindNgSelectEvent('openEvent')
  private readonly openEventSubject = new Subject<void>();

  readonly options: FieldOptions = {
    showCostCenter: true,
    hideRecentProjects: false,
  };

  private readonly includeCompletedProjectsInSearchSubject = new BehaviorSubject<boolean>(false);
  readonly includeCompletedProjectsInSearch$ = this.includeCompletedProjectsInSearchSubject.asObservable();
  readonly setIncludeCompletedProjectsInSearch = (value: boolean): void => this.includeCompletedProjectsInSearchSubject.next(value);

  private readonly searchOnlyInProjectsSubject = new BehaviorSubject<IrisProjectI['id'][]>(null);
  readonly searchOnlyInProjects = (projectsIds: IrisProjectI['id'][]): void => this.searchOnlyInProjectsSubject.next(projectsIds);

  private readonly excludeProjectsSubject = new BehaviorSubject<IrisProjectI['id'][]>(null);
  readonly excludeProjects = (projectsIds: number[] | null): void => this.excludeProjectsSubject.next(projectsIds);

  private readonly searchOnlyInProjectsWithSubject = new BehaviorSubject<FilterProjectsBy | null>(null);
  readonly searchOnlyInProjectsWith = (filterBy: FilterProjectsBy | null): void => this.searchOnlyInProjectsWithSubject.next(filterBy);

  private readonly includeInactiveProjectsSubject = new BehaviorSubject<boolean>(false);
  readonly setIncludeInactiveProjects = (value: boolean): void => this.includeInactiveProjectsSubject.next(value);

  readonly needScrollToTop = (prev: IrisProjectGroup[], curr: IrisProjectGroup[]): boolean => {
    const prevAll = prev?.find(x => x.code === 'ALL');
    const currAll = curr?.find(x => x.code === 'ALL');
    return prevAll?.projects?.length > currAll?.projects?.length;
  };

  readonly result$ = searchProcessor<IrisProjectGroup, ProjectSearchContext>(
    null,
    ctx => handleTypeahead$(ctx, this.typeahead),
    ctx => this.handleSearchOptions$(ctx),
    ctx => this.handleSearch$(ctx),
  );

  private handleSearchOptions$(ctx: ProjectSearchContext): Observable<ProjectSearchContext> {
    return combineLatest([
      this.includeCompletedProjectsInSearchSubject,
      this.searchOnlyInProjectsSubject,
      this.excludeProjectsSubject,
      this.searchOnlyInProjectsWithSubject,
      this.includeInactiveProjectsSubject,
    ]).pipe(
      debounceTime(0),
      map(([includeCompletedProjectsInSearch, searchOnlyInProjects, excludeProjects, filterBy, includeInactiveProjects]) => ({
        ...ctx ?? {},
        searchOptions: {
          includeCompletedProjectsInSearch,
          searchOnlyInProjects,
          excludeProjects,
          filterBy,
          includeInactiveProjects,
        },
      })),
    );
  }

  private handleSearch$(ctx: ProjectSearchContext): Observable<ProjectSearchContext> {
    return combineLatest([
      searchProcessor<IrisProjectI, ProjectSearchContext<IrisProjectI>>(
        { term: ctx.term, searchOptions: ctx.searchOptions },
        ctxProject => this.openEventSubject.pipe(startWith(null), map(() => ctxProject)),
        ctxProject => handleSearch$(ctxProject, term => this.searchRecentProjects$(term, ctxProject.searchOptions)),
      ),
      searchProcessor<IrisProjectI, ProjectSearchContext<IrisProjectI>>(
        { term: ctx.term, searchOptions: ctx.searchOptions },
        ctxProject => handleSearch$(ctxProject, term => this.searchProjects$(term, ctxProject.searchOptions)),
        ctxProject => handleInfiniteScroll$(ctxProject, this.scrollToEndSubject, (term, offset) => this.searchProjects$(term, ctxProject.searchOptions, offset)),
      ),
    ]).pipe(
      debounceTime(0),
      map(([recentProjects, projects]) => ({
        ...ctx ?? {},
        search: {
          count: projects?.count ?? 0,
          elements: [
            ...this.projectsGroup(recentProjects.elements, true),
            ...this.projectsGroup(projects.elements),
          ],
        },
      })),
    );
  }

  private projectsGroup(projects: IrisProjectI[], isRecent = false): IrisProjectGroup[] {
    return projects?.length ? [{
      code: isRecent ? 'RECENT' : 'ALL',
      name: this.translate.instant(isRecent ? 'label.RecentlyViewed' : 'label.AllProjects'),
      projects,
    }] : [];
  }

  private searchRecentProjects$(term: string, searchOptions: SearchOptions): Observable<IrisPage<IrisProjectI>> {
    if (this.options.hideRecentProjects) { return of(emptyIrisPage<IrisProjectI>()); }

    return this.globalSandbox.recentProjects$.pipe(
      map(projects => {
        const elements = projects
          .filter(project => !searchOptions.searchOnlyInProjects || searchOptions.searchOnlyInProjects.includes(project.id))
          .filter(project => !searchOptions.excludeProjects?.includes(project.id))
          .filter(project => searchOptions.includeInactiveProjects || project.phase !== PhaseNames.Inactive)
          .filter(project => !term ||
            project.name?.toLowerCase().includes(term.toLowerCase()) ||
            project.costCenter?.toLowerCase().includes(term.toLowerCase()) ||
            project.strathekNumber?.toString().includes(term));

        return { count: elements.length, elements };
      }),
      catchError(() => of(emptyIrisPage<IrisProjectI>())),
    );
  }

  private searchProjects$(term: string, searchOptions: SearchOptions, offset = 0): Observable<IrisPage<IrisProjectI>> {
    this.loadingSubject.next(true);
    return this.projectsApiService.getProjectsPage(this.buildSearchProjectsQueryParams(term, searchOptions, offset)).pipe(
      catchError(() => of(emptyIrisPage<IrisProjectI>())),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  private buildSearchProjectsQueryParams(term: string, options: SearchOptions, offset = 0): IrisQueryParams {
    const termIsNotEmpty = term != null && term !== '';
    return new IrisQueryParamsBuilder()
      .plugin(b => termIsNotEmpty
        ? b.filterOr([
          or => or.filter('name', [`%${term}%`], t => t.strict(false)),
          or => or.filter('costCenter', [`%${term}%`], t => t.strict(false)),
          or => or.filter('strathekNumber', [`%${term}%`], t => t.strict(false)),
        ])
        : b)
      .plugin(b => options.searchOnlyInProjects != null
        ? b.filter('id', options.searchOnlyInProjects, t => t.strict(false))
        : b)
      .plugin(b => options.excludeProjects != null
        ? b.filter('id', options.excludeProjects, t => t.strict(false).not())
        : b)
      .plugin(b => {
        if (!options.filterBy) return b;

        for (const [key, value] of objectEntries(options.filterBy)) {
          if (value != null) {
            b = b.filter(key, value, t => t.strict(false));
          }
        }

        return b;
      })
      .plugin(b => options.includeInactiveProjects ? b : b.filter('phase', [PhaseNames.Inactive], t => t.not()))
      .urlParam('kind', options.includeCompletedProjectsInSearch ? 'ALL' : 'ONGOING')
      .limit(PAGE_SIZE)
      .offset(offset)
      .orderBy('name')
      .onlyFields(ONLY_FIELDS)
      .toStructure();
  }

  getMissingItemLabelFn(projectId: IrisProjectI['id']): Observable<string> {
    if (!projectId) { return of(null); }

    const params = new IrisQueryParamsBuilder()
      .onlyFields(['name'])
      .toStructure();

    return this.projectsApiService.getById(projectId, params).pipe(
      map<IrisProjectI, string>(project => project.name),
      catchError(() => of(null)),
    );
  }
}
